import type {CustomerScanContext} from "../scan-model/CustomerScanContext.tsx";
import type {VersionScanPlan} from "../../../analysis-process/3-perform-scan/scan-operations/2-ScanVersions.tsx";
import type {OrchestrationJob} from "../../../metl-models/jobs/Orchestration.ts";
import type {TransformationJob} from "../../../metl-models/jobs/Transformation.ts";
import {runPromises} from "../../../util/promises/PromiseRunner.tsx";
import type {WorkloadJobHolder} from "../../3-workload-analysis/model/WorkloadJobHolder.ts";
import {getComponentsOfType} from "../../../metl-models/jobs/component-types/jobComponentTypeFinder.ts";
import {runJobComponent} from "../../../metl-models/jobs/component-types/runJobComponent.ts";
import type {MetlJobsTree} from "../../../metl-models/debug/MetlJobsTree.ts";
import type {SharedJobData} from "../../../analysis-process/3-perform-scan/scan-model/ScanContext.tsx";

type JobTypeString = "TRANSFORMATION" | "ORCHESTRATION"

function extractPathFromTree(tree: MetlJobsTree, jobName: string, currentPath: string[] = []): string[] | undefined {
    // Check if the job is in this tree node
    const job = tree.jobs.find(j => j.name === jobName)
    if (job) {
        return [...currentPath, tree.name]
    }
    
    // Recursively search children
    for (const child of tree.children) {
        const result = extractPathFromTree(child, jobName, [...currentPath, tree.name])
        if (result) return result
    }
    
    return undefined
}

function scanJobDetails(
    jobDebug: OrchestrationJob | TransformationJob,
    jobType: JobTypeString,
    jobName: string,
    path: string[],
    sharedJobsMap: Record<number, SharedJobData>
): WorkloadJobHolder {
    const linksTo = getComponentsOfType(jobDebug, runJobComponent).map(component => component.targetJobName)
    
    // Identify linked shared jobs by checking component implementation IDs
    const components = Object.values(jobDebug.components)
    const linkedSharedJobs = components
        .filter(component => sharedJobsMap[component.implementationID] !== undefined)
        .map(component => sharedJobsMap[component.implementationID]!)

    if(jobType === "TRANSFORMATION") {
        return {
            job: jobDebug as TransformationJob,
            name: jobName,
            path,
            type: jobType,
            linksTo,
            linkedSharedJobs
        }
    } else {
        return {
            job: jobDebug as OrchestrationJob,
            name: jobName,
            path,
            type: jobType,
            linksTo,
            linkedSharedJobs
        }
    }
}

async function downloadJobsIndividually(
    ctx: CustomerScanContext,
    versionScan: VersionScanPlan,
    sharedJobsMap: Record<number, SharedJobData>
): Promise<WorkloadJobHolder[]> {
    const {startAction, options} = ctx
    const {reportOne, complete} = startAction(`Scanning jobs in ${versionScan.id.groupName}/${versionScan.id.projectName}/${versionScan.id.versionName}`, versionScan.jobCount)

    // Get version debug to extract paths
    const versionDebug = await versionScan.api.debug()
    
    async function scan({job, jobType}: {job: string, jobType: JobTypeString}) {
        const jobDebug = await versionScan.api.jobs.get(job).debug()
        const path = extractPathFromTree(versionDebug.jobsTree, job) ?? []
        const result = scanJobDetails(jobDebug, jobType, job, path, sharedJobsMap)
        reportOne()
        return result
    }
    
    try {
        const items: {job: string, jobType: JobTypeString}[] = [
            ...versionScan.orchestrationJobs.map(x => ({job: x, jobType: "ORCHESTRATION" as JobTypeString})),
            ...versionScan.transformationJobs.map(x => ({job: x, jobType: "TRANSFORMATION" as JobTypeString}))
        ]
        return await runPromises(options.jobDownloadRunner, items, scan)
    } finally {
        complete()
    }
}

async function downloadJobsExport(
    ctx: CustomerScanContext,
    versionScan: VersionScanPlan,
    sharedJobsMap: Record<number, SharedJobData>
): Promise<WorkloadJobHolder[]> {
    const api = ctx.api.project(versionScan.id).versions.get(versionScan.id.versionName).jobs
    const jobExports = await api.exportAll()
    return jobExports.objects.map((jobExport) => {
        const jobDebug = jobExport.jobObject
        return scanJobDetails(jobDebug, jobExport.info.type, jobExport.info.name, jobExport.path, sharedJobsMap)
    })
}

export async function scanWorkloadJobs(
    ctx: CustomerScanContext,
    versionScan: VersionScanPlan,
    sharedJobsMap: Record<number, SharedJobData>
): Promise<WorkloadJobHolder[]> {
    const {logError} = ctx

    const scanFunction = ctx.options.jobDownloadStrategy === "INDIVIDUAL" ? downloadJobsIndividually : downloadJobsExport

    try {
        return await scanFunction(ctx, versionScan, sharedJobsMap)
    } catch (error) {
        logError(`Error scanning jobs in ${versionScan.id.groupName}/${versionScan.id.projectName}/${versionScan.id.versionName}.`, error)
        throw error
    }
}
